// [src/hooks/useAuthSession.jsx]

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from 'config/apiConfig';
import {
    getTokenStatus,
    clearAllAuthStorage,
    resetTokenExpiry,
    checkTokenIntegrity,
    updateTokenAndExpiry
} from 'utils/tokenManager';

export const useAuthSession = ({ requireAuth = true, redirectIfFound = false } = {}) => {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAndRecoverSession = useCallback(async () => {
        // 토큰 매니저가 복구해준 최신 상태 가져오기
        const { token, rtoken, isExpired, userInfo: localUserInfo } = getTokenStatus();

        // 1-1. 토큰 자체가 없으면 아웃
        if (!token || isExpired) {
            if (requireAuth) {
                clearAllAuthStorage();
                navigate('/', { replace: true });
            } else {
                setLoading(false);
            }
            return;
        }

        // 1-2. 로그인 페이지 접속 시 리다이렉트
        if (redirectIfFound) {
            navigate('/home', { replace: true });
            return;
        }

        // 일단 로컬 정보로 화면 먼저 표시 (Optimistic UI)
        if (localUserInfo) {
            setUserInfo(localUserInfo);
            setLoading(false);
        }

        // 서버에 최신 정보 요청 (검증 및 갱신)
        // [수정] fetch에 타임아웃 추가. 기존엔 응답이 오지 않으면 무한 대기하여
        // 로딩 화면이 끝나지 않던 문제(특정 회원의 느린/불안정한 네트워크 환경에서 체감). (2026-06-24)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초
        try {
            const response = await fetch(`${API_BASE_URL}/home`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal,
            });

            const resData = await response.json();
            const data = resData.data || resData;
            const serverUser = data.userInfo;

            // [핵심 방어 로직]
            if (serverUser && serverUser.userId && serverUser.userId !== "null") {
                // A. 서버가 정상 데이터를 줌 -> 최신 데이터로 갱신 및 동기화
                setUserInfo(serverUser);
                // [버그수정] 기존엔 (token, serverUser, true) 3개 인자만 전달되어
                // updateTokenAndExpiry(atoken, rtoken, userInfo, isAutoLogin) 시그니처에서
                // rtoken 자리에 serverUser가, userInfo 자리에 true가 밀려 들어가
                // userInfo가 매번 오염되던(문자열 "true"로 저장) 버그. rtoken을 정확히 전달하도록 수정. (2026-06-24)
                updateTokenAndExpiry(token, rtoken, serverUser, true);
            } else {
                // B. 서버가 200 OK지만 Null을 줌 (만료됨) -> 로컬 데이터 확인
                console.warn("⚠️ 서버 세션 만료됨(Null). 로컬 데이터로 유지합니다.");

                if (localUserInfo) {
                    // 로컬 데이터가 살아있으면 유지 (30일 정책)
                    // (여기서 아무것도 안 하면 기존 localUserInfo 사용됨)
                } else {
                    // 로컬도 없고 서버도 없으면 진짜 로그아웃
                    if (requireAuth) {
                        clearAllAuthStorage();
                        navigate('/', { replace: true });
                    }
                }
            }
        } catch (e) {
            console.error("세션 체크 중 통신 에러 (로컬 데이터 유지):", e);
            // 네트워크 에러 시에도 로컬 데이터가 있다면 로그아웃 시키지 않음
        } finally {
            clearTimeout(timeoutId);
        }

        setLoading(false);
    }, [navigate, requireAuth, redirectIfFound]);

    useEffect(() => {
        checkAndRecoverSession();

        const handleFocus = () => checkAndRecoverSession();
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') handleFocus();
        });

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleFocus);
        };
    }, [checkAndRecoverSession]);

    return { userInfo, loading };
};